import React, { useState, useEffect } from 'react';
import { Box, Tabs, Tab, CircularProgress, IconButton, Chip } from '@mui/material';
import { Refresh as RefreshIcon, Warning as WarningIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { adminAPI } from '../../utils/api';

// Import all tab components
import OverviewTab from './CommandCenter/OverviewTab';
import QueueMonitorTab from './CommandCenter/QueueMonitorTab';
import ErrorTrackingTab from './CommandCenter/ErrorTrackingTab';
import UserActivityTab from './CommandCenter/UserActivityTab';
import ManagerIntelAnalyticsTab from './CommandCenter/ManagerIntelAnalyticsTab';
import OrphanMarketplaceTab from './CommandCenter/OrphanMarketplaceTab';
import SystemHealthTab from './CommandCenter/SystemHealthTab';
import LiveActivityTab from './CommandCenter/LiveActivityTab';
import AdminActionsTab from './CommandCenter/AdminActionsTab';
import AnalyticsTab from './CommandCenter/AnalyticsTab';
import SimulationsTab from './CommandCenter/SimulationsTab';
import FeatureStatsTab from './CommandCenter/FeatureStatsTab';
import AnalystReviewsTab from './CommandCenter/AnalystReviewsTab';

const CommandCenter = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [timeSeries, setTimeSeries] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Fetch data function
  const fetchData = async () => {
    try {
      const [metricsRes, timeSeriesRes] = await Promise.all([
        adminAPI.getMetrics(),
        adminAPI.getTimeSeries(14),
      ]);

      setMetrics(metricsRes.data);
      setTimeSeries(timeSeriesRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    }
  };

  // Handle refresh button
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  };

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchData();
    }, 30000);

    return () => clearInterval(interval);
  }, [autoRefresh]);

  useEffect(() => {
    // Redirect if not admin
    if (isAuthenticated && user && !user.isAdmin) {
      navigate('/');
      return;
    }

    if (!isAuthenticated) {
      navigate('/');
      return;
    }

    // Initial data load
    const loadData = async () => {
      await fetchData();
      setLoading(false);
    };

    if (user?.isAdmin) {
      loadData();
    }
  }, [user, isAuthenticated, navigate]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: '#0F172A',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress sx={{ color: '#3B82F6' }} />
      </Box>
    );
  }

  if (!metrics || !timeSeries) {
    return null;
  }

  const tabs = [
    { label: 'Analyst Reviews', icon: '🧾', critical: false },
    { label: 'Simulations', icon: '🎲', critical: false },
    { label: 'Manager Intel', icon: '🧠', critical: false },
    { label: 'Feature Stats', icon: '🪧', critical: false },
    { label: 'Overview', icon: '📊', critical: false },
    { label: 'Users', icon: '👥', critical: false },
    { label: 'Visitors', icon: '📈', critical: false },
    { label: 'Marketplace', icon: '🏪', critical: false },
    { label: 'Queue Monitor', icon: '⚙️', critical: metrics.queues.managerIntelSync.failed > 0 },
    { label: 'Errors', icon: '🚨', critical: metrics.managerIntel.errorCount > 0 },
    { label: 'System Health', icon: '❤️', critical: false },
    { label: 'Live Feed', icon: '📡', critical: false },
    { label: 'Actions', icon: '⚡', critical: false },
  ];

  const criticalCount = tabs.filter(t => t.critical).length;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
        color: '#F1F5F9',
        padding: '0',
      }}
    >
      {/* Top Bar */}
      <Box
        sx={{
          background: '#1E293B',
          borderBottom: '2px solid #334155',
          padding: '16px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Box
            sx={{
              fontSize: '24px',
              fontWeight: 700,
              background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '0.5px',
            }}
          >
            COMMAND CENTER
          </Box>
          {criticalCount > 0 && (
            <Chip
              icon={<WarningIcon sx={{ fontSize: '16px' }} />}
              label={`${criticalCount} Alert${criticalCount > 1 ? 's' : ''}`}
              sx={{
                background: '#EF4444',
                color: '#FFF',
                fontWeight: 600,
                fontSize: '12px',
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.7 },
                },
              }}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {/* Auto-refresh indicator */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '13px',
              color: autoRefresh ? '#10B981' : '#6B7280',
              cursor: 'pointer',
              userSelect: 'none',
            }}
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            <Box
              sx={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: autoRefresh ? '#10B981' : '#6B7280',
                animation: autoRefresh ? 'blink 2s infinite' : 'none',
                '@keyframes blink': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.3 },
                },
              }}
            />
            Auto-refresh {autoRefresh ? 'ON' : 'OFF'}
          </Box>

          {/* Refresh button */}
          <IconButton
            onClick={handleRefresh}
            disabled={refreshing}
            sx={{
              background: '#334155',
              color: '#F1F5F9',
              width: '40px',
              height: '40px',
              '&:hover': {
                background: '#475569',
              },
              '&:disabled': {
                background: '#1E293B',
                color: '#475569',
              },
            }}
          >
            <RefreshIcon
              sx={{
                animation: refreshing ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }}
            />
          </IconButton>

          {/* User info */}
          <Box
            sx={{
              fontSize: '13px',
              color: '#94A3B8',
              paddingLeft: '16px',
              borderLeft: '1px solid #334155',
            }}
          >
            {user?.email}
          </Box>
        </Box>
      </Box>

      {/* Tabs */}
      <Box
        sx={{
          background: '#1E293B',
          borderBottom: '1px solid #334155',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              color: '#94A3B8',
              fontWeight: 600,
              fontSize: '13px',
              textTransform: 'none',
              minHeight: '56px',
              padding: '12px 20px',
              '&.Mui-selected': {
                color: '#3B82F6',
              },
            },
            '& .MuiTabs-indicator': {
              background: 'linear-gradient(90deg, #3B82F6 0%, #8B5CF6 100%)',
              height: '3px',
            },
          }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>{tab.icon}</span>
                  {tab.label}
                  {tab.critical && (
                    <Box
                      sx={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: '#EF4444',
                      }}
                    />
                  )}
                </Box>
              }
            />
          ))}
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box sx={{ padding: '24px' }}>
        {activeTab === 0 && <AnalystReviewsTab />}
        {activeTab === 1 && <SimulationsTab />}
        {activeTab === 2 && <ManagerIntelAnalyticsTab metrics={metrics} timeSeries={timeSeries} />}
        {activeTab === 3 && <FeatureStatsTab />}
        {activeTab === 4 && <OverviewTab metrics={metrics} timeSeries={timeSeries} />}
        {activeTab === 5 && <UserActivityTab metrics={metrics} />}
        {activeTab === 6 && <AnalyticsTab />}
        {activeTab === 7 && <OrphanMarketplaceTab metrics={metrics} timeSeries={timeSeries} />}
        {activeTab === 8 && <QueueMonitorTab metrics={metrics} />}
        {activeTab === 9 && <ErrorTrackingTab metrics={metrics} />}
        {activeTab === 10 && <SystemHealthTab metrics={metrics} />}
        {activeTab === 11 && <LiveActivityTab metrics={metrics} />}
        {activeTab === 12 && <AdminActionsTab onRefresh={handleRefresh} />}
      </Box>
    </Box>
  );
};

export default CommandCenter;
